import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BellRing, CheckCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { docketFollowService } from '../lib/docketFollowService';
import { SimpleDocketAutocomplete } from '../components/SimpleDocketAutocomplete';
import { CURATED_DOCKETS } from '../content/curatedDockets';
import { track } from '../utils/analytics';
import {
  topicSubscriptionService,
  type TopicBundle,
} from '../lib/topicSubscriptionService';

type PageState =
  | { kind: 'loading' }
  | { kind: 'auto_following'; docketNumber: string }
  | { kind: 'auto_followed'; docketNumber: string; label: string }
  | { kind: 'picker' }
  | { kind: 'redirecting_unauth' };

const ONBOARDING_COMPLETE_KEY = 'commsgenie_onboarding_completed';

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const followParam = searchParams.get('follow');

  // Supabase appends #type=signup for brand-new accounts and
  // #type=magiclink for existing users logging back in.
  // We read it once on mount; after Supabase processes the hash it's gone.
  const authType = useMemo(() => {
    const hash = window.location.hash;
    const m = hash.match(/[#&]type=([^&]+)/);
    return m ? m[1] : null;   // 'signup' | 'magiclink' | null
  }, []);

  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [searchInput, setSearchInput] = useState('');
  const [followingDocket, setFollowingDocket] = useState<string | null>(null);
  const [proceedingNames, setProceedingNames] = useState<Record<string, string>>({});
  const [bundles, setBundles] = useState<TopicBundle[]>([]);
  const [subscribingBundle, setSubscribingBundle] = useState<string | null>(null);
  const [subscribedBundles, setSubscribedBundles] = useState<Set<string>>(new Set());
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (message: string, ms = 3500) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), ms);
  };

  // Bootstrap: check auth, and if ?follow=<dn> is present, auto-follow.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        if (cancelled) return;
        setState({ kind: 'redirecting_unauth' });
        // The magic-link session should be materialized by the time this
        // page mounts (Supabase detectSessionInUrl=true). If it isn't,
        // bounce them to home; they can try the flow again.
        navigate('/', { replace: true });
        return;
      }

      if (followParam) {
        if (cancelled) return;
        setState({ kind: 'auto_following', docketNumber: followParam });

        try {
          await docketFollowService.follow(followParam);

          // Look up a friendly name for the toast; fall back to the dn.
          const { data: proc } = await supabase
            .from('open_proceedings')
            .select('proceeding_name')
            .eq('docket_number', followParam)
            .maybeSingle();

          const label = proc?.proceeding_name || `Docket ${followParam}`;

          if (cancelled) return;
          localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
          track('docket_auto_followed_via_magic_link', {
            docket_number: followParam,
          });
          setState({ kind: 'auto_followed', docketNumber: followParam, label });

          // Brief confirmation, then bounce to the docket detail page.
          setTimeout(() => {
            if (cancelled) return;
            navigate(`/proceedings/${encodeURIComponent(followParam)}`, {
              replace: true,
            });
          }, 1800);
        } catch (err) {
          console.error('Auto-follow failed:', err);
          if (cancelled) return;
          // Fall back to picker so user can retry manually.
          setState({ kind: 'picker' });
        }
        return;
      }

      // No ?follow param.
      //
      // Routing rules:
      //   • Existing user logging in (type=magiclink) → home page.
      //     They didn't ask for onboarding; just get them in.
      //   • Brand-new signup (type=signup) with no follows → show picker.
      //   • Brand-new signup that somehow already has follows → home page.
      //     (Edge case: invited to a team, or signed up twice.)
      if (authType === 'magiclink') {
        if (cancelled) return;
        navigate('/', { replace: true });
        return;
      }

      // type=signup (or unknown) — check whether they've already followed
      // anything so we don't show the picker to someone who's been here before.
      try {
        const followed = await docketFollowService.getAllFollowedDocketNumbers();
        if (cancelled) return;
        if (followed.size > 0) {
          // Already set up — just go home.
          navigate('/', { replace: true });
          return;
        }
      } catch (err) {
        // Non-fatal — if the lookup fails, fall back to showing the picker.
        console.warn('Onboarding follow-count check failed:', err);
      }

      if (cancelled) return;
      setState({ kind: 'picker' });
    };

    run();

    return () => {
      cancelled = true;
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [followParam, navigate, authType]);

  // Warm up the proceeding names for the curated list so we can render a
  // friendly toast on follow.
  useEffect(() => {
    if (state.kind !== 'picker') return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('open_proceedings')
          .select('docket_number, proceeding_name')
          .in(
            'docket_number',
            CURATED_DOCKETS.map((d) => d.docket_number)
          );
        if (cancelled || !data) return;
        const map: Record<string, string> = {};
        for (const row of data) {
          if (row.docket_number && row.proceeding_name) {
            map[row.docket_number] = row.proceeding_name;
          }
        }
        setProceedingNames(map);
      } catch {
        // Non-fatal — labels fall back to the curated list's built-in labels.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.kind]);

  // Load the curated topic bundles for the "what do you want to keep up
  // with?" step. Best-effort — if it fails, the docket-search path below
  // still works.
  useEffect(() => {
    let cancelled = false;
    topicSubscriptionService
      .getBundles()
      .then((b) => {
        if (!cancelled) setBundles(b);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFollowDocket = async (docketNumber: string, label: string) => {
    try {
      setFollowingDocket(docketNumber);
      await docketFollowService.follow(docketNumber);
      localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
      track('docket_followed_via_onboarding', { docket_number: docketNumber });
      showToast(`Following ${label}. You'll get your first digest tomorrow morning.`);
      setTimeout(() => navigate('/watchlist'), 1200);
    } catch (err) {
      console.error('Follow failed:', err);
      showToast('Could not follow that docket. Please try again.');
    } finally {
      setFollowingDocket(null);
    }
  };

  // Subscribe to a curated interest: creates a standing interest (so future
  // matching proceedings auto-surface) AND seed-follows its current dockets.
  const handleSubscribeBundle = async (bundle: TopicBundle) => {
    try {
      setSubscribingBundle(bundle.key);
      const result = await topicSubscriptionService.subscribe(bundle.key);
      localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
      track('topic_bundle_subscribed_via_onboarding', {
        bundle_key: bundle.key,
        followed_count: result?.followed_dockets?.length ?? 0,
      });
      setSubscribedBundles((prev) => new Set(prev).add(bundle.key));
      const n = result?.followed_dockets?.length ?? 0;
      showToast(
        n > 0
          ? `Following ${bundle.label}. We'll also flag new proceedings that match.`
          : `Subscribed to ${bundle.label}. We'll alert you when a matching proceeding opens.`
      );
    } catch (err) {
      console.error('Subscribe failed:', err);
      showToast('Could not subscribe. Please try again.');
    } finally {
      setSubscribingBundle(null);
    }
  };

  const handleSearchSubmit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    // Hand off to the proceeding detail page where the user can follow.
    navigate(`/proceedings/${encodeURIComponent(trimmed)}`);
  };

  const curatedWithNames = useMemo(
    () =>
      CURATED_DOCKETS.map((d) => ({
        ...d,
        resolvedLabel: proceedingNames[d.docket_number] || d.label,
      })),
    [proceedingNames]
  );

  if (state.kind === 'loading' || state.kind === 'redirecting_unauth') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (state.kind === 'auto_following') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center">
        <Loader className="h-8 w-8 animate-spin text-emerald-600" />
        <p className="text-sm text-gray-600">
          Setting you up to follow Docket {state.docketNumber}…
        </p>
      </div>
    );
  }

  if (state.kind === 'auto_followed') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle className="h-7 w-7 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-cg-ink">
            You're following {state.label}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            You'll get your first digest tomorrow morning. Taking you to the docket…
          </p>
        </div>
      </div>
    );
  }

  // state.kind === 'picker'
  return (
    <div className="relative mx-auto max-w-2xl py-8 sm:py-12">
      {toast && (
        <div className="fixed left-1/2 top-6 z-50 -translate-x-1/2 rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-3 text-sm font-medium text-emerald-800 shadow-lg">
          {toast}
        </div>
      )}

      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white">
          <BellRing className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-cg-ink">
            What do you want to keep up with?
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Pick an area and we'll follow the active proceedings for you — and
            flag new ones the moment they open. You'll get a digest whenever
            something moves.
          </p>
        </div>
      </div>

      {bundles.length > 0 && (
        <div className="mb-8">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {bundles.map((bundle) => {
              const isSubscribing = subscribingBundle === bundle.key;
              const isSubscribed = subscribedBundles.has(bundle.key);
              return (
                <button
                  key={bundle.key}
                  onClick={() => handleSubscribeBundle(bundle)}
                  disabled={!!subscribingBundle || isSubscribed}
                  className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-all hover:border-emerald-300 hover:bg-emerald-50/30 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-cg-ink">
                      {bundle.label}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-600">
                      {bundle.blurb}
                    </div>
                  </div>
                  {isSubscribing ? (
                    <Loader className="h-4 w-4 flex-shrink-0 animate-spin text-emerald-600" />
                  ) : isSubscribed ? (
                    <CheckCircle className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                  ) : null}
                </button>
              );
            })}
          </div>
          {subscribedBundles.size > 0 && (
            <button
              onClick={() => navigate('/watchlist')}
              className="mt-4 w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              Done — go to my watchlist
            </button>
          )}
        </div>
      )}

      <div className="mb-3">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Or follow a specific docket
        </h2>
        <SimpleDocketAutocomplete
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={handleSearchSubmit}
          placeholder="Enter docket number or description (e.g., 22-271, net neutrality)"
          className="w-full"
        />
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Or pick from popular proceedings
        </h2>
        <div className="space-y-2">
          {curatedWithNames.map((docket) => {
            const isLoading = followingDocket === docket.docket_number;
            return (
              <button
                key={docket.docket_number}
                onClick={() =>
                  handleFollowDocket(docket.docket_number, docket.resolvedLabel)
                }
                disabled={!!followingDocket}
                className="flex w-full items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-all hover:border-emerald-300 hover:bg-emerald-50/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-cg-ink">
                      {docket.docket_number}
                    </span>
                    <span className="truncate text-sm text-gray-700">
                      {docket.resolvedLabel}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">{docket.description}</p>
                </div>
                <div className="shrink-0 text-emerald-700">
                  {isLoading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <BellRing className="h-4 w-4" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 text-center">
        <button
          type="button"
          onClick={() => navigate('/proceedings')}
          className="text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          Browse all proceedings instead
        </button>
      </div>
    </div>
  );
};
